import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

/**
 * TemplateLayout - Reusable three-column layout for workflow templates
 *
 * Features:
 * - Header with back button and title
 * - Three-column layout: left (config), middle (mobile preview), right (thinking animation)
 * - Responsive design with proper scrolling
 * - Mobile view with tab switching
 *
 * @param {object} props
 * @param {string} props.title - Page title
 * @param {string} props.subtitle - Optional subtitle/description
 * @param {React.ReactNode} props.leftPanel - Content for left configuration panel
 * @param {React.ReactNode} props.middlePanel - Content for middle mobile preview panel
 * @param {React.ReactNode} props.rightPanel - Content for right thinking animation panel
 * @param {string} props.backPath - Path to navigate back to (default: '/templates')
 * @param {boolean} props.isGenerating - Whether content is being generated
 * @param {boolean} props.hasGeneratedContent - Whether content has been generated
 */
function TemplateLayout({
  title,
  subtitle,
  leftPanel,
  middlePanel,
  rightPanel,
  backPath = '/templates',
  isGenerating = false,
  hasGeneratedContent = false
}) {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileView, setMobileView] = useState('form'); // 'form', 'animation', 'preview'

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-switch to animation view when generating starts
  useEffect(() => {
    if (isMobile && isGenerating) {
      setMobileView('animation');
    }
  }, [isMobile, isGenerating]);

  // Auto-switch to preview view when generation completes
  useEffect(() => {
    if (isMobile && !isGenerating && hasGeneratedContent && mobileView === 'animation') {
      setMobileView('preview');
    }
  }, [isMobile, isGenerating, hasGeneratedContent, mobileView]);

  // Mobile view rendering
  if (isMobile) {
    return (
      <div style={{
        minHeight: '100vh',
        height: '100vh',
        background: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Mobile Header */}
        <div style={{
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          padding: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          flexShrink: 0
        }}>
          <button
            onClick={() => navigate(backPath)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px'
            }}
          >
            <Icon icon="mdi:arrow-left" style={{ fontSize: 'var(--font-xl)', color: '#6b7280' }} />
          </button>

          <div style={{ flex: 1 }}>
            <h1 style={{
              margin: 0,
              fontSize: 'var(--font-base)',
              fontWeight: '700',
              color: '#1e293b'
            }}>
              {title}
            </h1>
          </div>
        </div>

        {/* Mobile Tab Navigation */}
        <div style={{
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          flexShrink: 0
        }}>
          <button
            onClick={() => setMobileView('form')}
            style={{
              flex: 1,
              padding: '0.875rem',
              background: 'transparent',
              border: 'none',
              borderBottom: mobileView === 'form' ? '2px solid #3b82f6' : '2px solid transparent',
              color: mobileView === 'form' ? '#3b82f6' : '#64748b',
              fontSize: 'var(--font-xs)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <Icon icon="mdi:form-select" style={{ fontSize: 'var(--font-base)', marginRight: '0.5rem' }} />
            Form
          </button>

          <button
            onClick={() => setMobileView('animation')}
            style={{
              flex: 1,
              padding: '0.875rem',
              background: 'transparent',
              border: 'none',
              borderBottom: mobileView === 'animation' ? '2px solid #3b82f6' : '2px solid transparent',
              color: mobileView === 'animation' ? '#3b82f6' : '#64748b',
              fontSize: 'var(--font-xs)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <Icon icon="mdi:brain" style={{ fontSize: 'var(--font-base)', marginRight: '0.5rem' }} />
            Process
          </button>

          <button
            onClick={() => setMobileView('preview')}
            style={{
              flex: 1,
              padding: '0.875rem',
              background: 'transparent',
              border: 'none',
              borderBottom: mobileView === 'preview' ? '2px solid #3b82f6' : '2px solid transparent',
              color: mobileView === 'preview' ? '#3b82f6' : '#64748b',
              fontSize: 'var(--font-xs)',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <Icon icon="mdi:video" style={{ fontSize: 'var(--font-base)', marginRight: '0.5rem' }} />
            Record
          </button>
        </div>

        {/* Mobile Content */}
        <div style={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative'
        }}>
          {/* Form View */}
          <div
            className="mobile-form-view"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflowY: 'auto',
              padding: '1.5rem',
              display: mobileView === 'form' ? 'block' : 'none'
            }}
          >
            <style>{`
              .mobile-form-view > div > div:last-child {
                padding-left: 0 !important;
              }
            `}</style>
            {leftPanel}
          </div>

          {/* Animation View */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: 'auto',
            display: mobileView === 'animation' ? 'block' : 'none'
          }}>
            {rightPanel}
          </div>

          {/* Preview View */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: 'auto',
            display: mobileView === 'preview' ? 'block' : 'none'
          }}>
            {middlePanel}
          </div>
        </div>
      </div>
    );
  }

  // Desktop view rendering
  return (
    <div style={{
      minHeight: '100vh',
      height: '100%',
      background: '#f8f9fa',
      display: 'flex',
      overflow: 'hidden'
    }}>
      {/* Left Panel - Configuration (1/3) */}
      <div style={{
        width: '33.33%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRight: '1px solid #e5e7eb'
      }}>
        {/* Header - Only on Left Side */}
        <div style={{
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexShrink: 0
        }}>
          {/* Back Button */}
          <button
            onClick={() => navigate(backPath)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <Icon icon="mdi:arrow-left" style={{ fontSize: 'var(--font-xl)', color: '#6b7280' }} />
          </button>

          {/* Title Section */}
          <div style={{ flex: 1 }}>
            <h1 style={{
              margin: 0,
              fontSize: 'var(--font-xl)',
              fontWeight: '700',
              color: '#1e293b'
            }}>
              {title}
            </h1>
            {subtitle && (
              <p style={{
                margin: '0.25rem 0 0 0',
                fontSize: 'var(--font-xxs)',
                color: '#64748b'
              }}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Left Panel Content - Scrollable */}
        <div style={{
          flex: 1,
          padding: '2rem',
          paddingBottom: '4rem',
          overflowY: 'auto'
        }}>
          {leftPanel}
        </div>
      </div>

      {/* Middle Panel - Mobile Preview (1/3) */}
      <div style={{
        width: '33.33%',
        background: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: '1px solid #e5e7eb'
      }}>
        {middlePanel}
      </div>

      {/* Right Panel - Thinking Animation (1/3) */}
      <div style={{
        width: '33.33%',
        background: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {rightPanel}
      </div>
    </div>
  );
}

export default TemplateLayout;
